import type { User, UserManager } from 'oidc-client-ts';

type Manager = Pick<UserManager, 'events' | 'signinSilent' | 'signinRedirect' | 'removeUser'>;

/** Only the signed-in workspaces need a session; public pages work signed out. */
export const needsSignIn = (path: string) => /^\/(app|ops)(\/|$|\?|#)/.test(path);

/**
 * Keeps the page's access token current. Keycloak access tokens last five
 * minutes while the sign-in session lasts hours, so the token is renewed in
 * the background with the refresh token and every renewed token reaches the
 * page. If renewal fails — the session ended or the refresh token was revoked —
 * the stored session is forgotten. Inside the workspaces the person is sent to
 * sign in and brought back to where they were, instead of the page submitting
 * an expired token; on a public page they simply carry on signed out.
 */
export function keepSessionCurrent(manager: Manager, onUser: (user: User | null) => void, returnTo: () => string): () => void {
  let recovering = false;
  const loaded = (user: User) => onUser(user);
  const recover = async () => {
    if (recovering) return;
    recovering = true;
    const user = await manager.signinSilent().catch(() => null);
    if (user) { onUser(user); recovering = false; return; }
    onUser(null);
    await manager.removeUser().catch(() => undefined);
    const path = returnTo();
    if (!needsSignIn(path)) { recovering = false; return; }
    // The page is leaving for sign-in; stay guarded so a second event cannot redirect again.
    await manager.signinRedirect({ state: { returnTo: path } });
  };
  const expired = () => void recover();
  manager.events.addUserLoaded(loaded);
  manager.events.addAccessTokenExpired(expired);
  manager.events.addSilentRenewError(expired);
  return () => {
    manager.events.removeUserLoaded(loaded);
    manager.events.removeAccessTokenExpired(expired);
    manager.events.removeSilentRenewError(expired);
  };
}
